import React, { ChangeEvent, useEffect, useState } from 'react';
import { useVesselStore } from '../../../context/VesselContext';
import { TableWrapper } from './CargoTempLogReporting.style';
import CargoTemperatureConfigComponent from '../cargo-temp-log-config/CargoTempConfig';
import { CargoTemperatureControlVM, SortColumnEnum, TankDetails, getComparison, tabEnum, validateCargoTempLog } from '../CargoTemperatureLogUtils';
import sortUtils from '../../../utils/sortUtils';
import { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { Wrapper } from '../../../pages/cargo-temp-log/cargoTempLog.styles';
import { useCargoTempLogStore } from '../../../context/CargoTempLogContext';
import { CargoTemperatureLogEntry } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureLog';
import useCargoTempLogApi from '../../../api/cargo-temp-log/useCargoTempLogApi';
import CargoTempLogExport from '../cargo-temp-log-export/CargoTempLogExport';
import { formatAolUrlDate } from '../../../utils/aol/aolDateUtils';
import { InfoText } from '../../shared/info-text/InfoText.style';
import CargoTempLogStatusIcon from '../../ui/status-icon/CargoTempLogStatusIcon';
import { CargoTempLogStatusEnum } from '../../../enums/cargoTempLogEnum';
import { CargoTemperatureLogDate } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import { CargoTempLogOxygenUnitEnum } from '../../../enums/cargoTempLogOxygenUnitEnum';
import { isDateSame, getDateFromISO, dateToUtc } from 'odfjell-date-time-utils/dateTimeUtils';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { formatDateTime, DateTimeFormat } from 'odfjell-date-time-utils/dateTimeFormatter';
import CargoLogHeader from './CargoLogHeader';
import CargoTempLogTable from './CargoTempLogTable';

type Props = {
    imoNumber: number;
    populatedCargoTempLogDates: CargoTemperatureLogDate[];
};

const initialVM: CargoTemperatureControlVM = {
    cargoTanks: [],
};

const CargoTempLogReportingComponent: React.FC<Props> = ({ populatedCargoTempLogDates }) => {
    const { vessel } = useVesselStore();
    const {
        cargoTemperatureConfigs,
        cargoTemperatureInputFields,
        selectedCargoTempDate,
        selectedTab,
        setSelectedTab,
        isCommentsVisible,
        hasMissingConfigs,
        setHasMissingConfigs,
        setCargoTempLogReportLog,
    } = useCargoTempLogStore();
    const { compareTankNames } = sortUtils();
    const { getCargoTemperatureLog } = useCargoTempLogApi();
    const { isLoadingType } = useApiLoaderHelper();
    const { checkIsCargoTempLogReadOnly } = useReadOnlyUtils();
    const [vm, setVM] = useState<CargoTemperatureControlVM>(initialVM);
    const [sortColumn, setSortColumn] = useState<SortColumnEnum>(SortColumnEnum.CargoNr);
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [activeLogSchedules, setActiveLogSchedules] = useState<string[]>(['12:00']);
    const [editComment, setEditComment] = useState<boolean>(false);
    const [editCommentCargoLog, setEditCommentCargoLog] = useState<CargoTemperatureLogEntry>(null);
    const [commentInput, setCommentInput] = useState<string>('');
    const [cargoTemperatureLog, setCargoTemperatureLog] = useState<CargoTemperatureLogEntry[]>(null);
    const [hasDayCargo, setHasDayCargo] = useState<boolean | null>(null);
    const [hasCargoDates, setHasCargoDates] = useState<boolean | null>(null);

    useEffect(() => {
        if (hasMissingConfigs) setSelectedTab(tabEnum.config);
    }, [hasMissingConfigs]);

    useEffect(() => {
        if (populatedCargoTempLogDates && populatedCargoTempLogDates.length > 0 && selectedCargoTempDate) {
            const _cargoTempLogDate = populatedCargoTempLogDates.find((d) => isDateSame(d.logDate, selectedCargoTempDate, 'day'));
            const cargo = _cargoTempLogDate?.cargoInTankList?.map((c) => c.cargoNumber);

            if (!cargo || cargo.length === 0) {
                setHasDayCargo(false);
            } else {
                setHasDayCargo(true);
            }

            setCargoTempLogReportLog(_cargoTempLogDate?.cargoTemperatureLog ?? []);
            setCargoTemperatureLog(_cargoTempLogDate?.cargoTemperatureLog ? _cargoTempLogDate.cargoTemperatureLog : []);
            if (_cargoTempLogDate?.cargoTemperatureLog === null || _cargoTempLogDate?.cargoTemperatureLog?.length === 0) {
                getCargoTemperatureLog(_cargoTempLogDate.imoNumber, formatAolUrlDate(_cargoTempLogDate.logDate));
            }
            setHasCargoDates(true);
        }
        if (!populatedCargoTempLogDates || populatedCargoTempLogDates.length === 0) {
            setHasCargoDates(false);
        }
    }, [populatedCargoTempLogDates, selectedCargoTempDate]);

    useEffect(() => {
        if (
            !populatedCargoTempLogDates ||
            !vessel ||
            !cargoTemperatureConfigs ||
            !cargoTemperatureLog ||
            !cargoTemperatureInputFields ||
            cargoTemperatureInputFields.length < 1
        )
            return;
        if (hasMissingConfigs) setSelectedTab(tabEnum.config);
        initVM();
    }, [vessel, cargoTemperatureConfigs, cargoTemperatureLog, populatedCargoTempLogDates]);

    useEffect(() => {
        if (!vm.cargoTanks) {
            setHasMissingConfigs(false);
            return;
        }
        updateActiveSchedule();
        checkConfig(vm?.cargoTanks);
    }, [vm?.cargoTanks, cargoTemperatureConfigs, vessel]);

    useEffect(() => {
        if (!vm?.cargoTanks || vm.cargoTanks.length < 2) return;
        let rows;
        if (sortColumn === SortColumnEnum.CargoNr) {
            rows = vm.cargoTanks.sort((a, b) => getComparison(sortDesc, a.cargoNumber, b.cargoNumber, a.tank.tankName, b.tank.tankName));
        } else if (sortColumn === SortColumnEnum.TradeName) {
            rows = vm.cargoTanks.sort((a, b) => getComparison(sortDesc, a.tradeName, b.tradeName, a.tank.tankName, b.tank.tankName));
        } else if (sortColumn === SortColumnEnum.Category) {
            rows = vm.cargoTanks.sort((a, b) => (sortDesc ? (a.category > b.category ? -1 : 1) : a.category > b.category ? 1 : -1));
        } else {
            rows = vm.cargoTanks.sort((a, b) => compareTankNames(a.tank.tankName, b.tank.tankName, !sortDesc));
        }
        vm.cargoTanks = rows;

        setVM({ ...vm });
    }, [sortColumn, sortDesc]);

    const isReadOnly = checkIsCargoTempLogReadOnly();

    const checkConfig = (cargoTanks: TankDetails[]) => {
        if (!cargoTemperatureConfigs || !vessel) return;
        let _hasMissingConfig = false;

        cargoTanks.forEach((cargoTank) => {
            const config = cargoTemperatureConfigs.find((c) => c.cargoNumber === cargoTank.cargoNumber);
            if (!config) {
                _hasMissingConfig = true;
                return;
            }
        });

        setHasMissingConfigs(_hasMissingConfig);
    };

    const updateActiveSchedule = () => {
        if (!cargoTemperatureConfigs || cargoTemperatureConfigs.length === 0) return;
        const existingSchedules = new Set<string>([]);
        cargoTemperatureConfigs
            .filter((c) => vm.cargoTanks.findIndex((cargo) => cargo.cargoNumber === c.cargoNumber && c.inputFields.length > 0) !== -1)
            .forEach((c) => c.logTimes.forEach((l) => existingSchedules.add(l)));
        setActiveLogSchedules(Array.from(existingSchedules.values()).sort());
    };

    const initVM = () => {
        if (!vessel || !cargoTemperatureConfigs || !cargoTemperatureInputFields || !populatedCargoTempLogDates || !selectedCargoTempDate) return;

        const cargoTempLogDate = populatedCargoTempLogDates.find((c) => isDateSame(c.logDate, selectedCargoTempDate, 'day'));
        const _cargoTanks = cargoTempLogDate?.cargoInTankList?.reduce((list, cargoInTankItem) => {
            const inputFields = getInputFields(cargoInTankItem.cargoNumber);

            const tank = vessel.cargoTanks.find((c) => c.tankId === cargoInTankItem.tankId);

            if (tank) {
                const logTimes = getLogTimes(cargoInTankItem.cargoNumber) ?? [];
                const logs = logTimes.map((logTime) => {
                    const _cargoTemperatureLogs = inputFields.map((inputField) => {
                        const loggedTimeStr = formatDateTime(selectedCargoTempDate, DateTimeFormat.queryFormats.dateOnly) + `T${logTime}`;
                        const local = getDateFromISO(loggedTimeStr);
                        const utc = dateToUtc(local);

                        const existingCargoTemperatureLog = cargoTemperatureLog.find(
                            (c) =>
                                c.cargoNumber === cargoInTankItem.cargoNumber &&
                                c.tankId === tank.tankId &&
                                c.cargoTemperatureFieldId === inputField.cargoTemperatureInputFieldId &&
                                isDateSame(c.loggedTimeLocalTime, local, 'hour')
                        );
                        const ctl: CargoTemperatureLogEntry = {
                            cargoNumber: cargoInTankItem.cargoNumber,
                            cargoTemperatureLogId: existingCargoTemperatureLog?.cargoTemperatureLogId ?? 0,
                            logDateId: existingCargoTemperatureLog?.logDateId ?? null,
                            tankId: tank.tankId,
                            cargoTemperatureFieldId: inputField.cargoTemperatureInputFieldId,
                            loggedTimeLocalTime: local,
                            loggedTimeUTC: utc,
                            value: existingCargoTemperatureLog?.value,
                            isOnBoard: existingCargoTemperatureLog?.isOnBoard === undefined ? true : existingCargoTemperatureLog?.isOnBoard,
                            comment: existingCargoTemperatureLog?.comment,
                        };
                        return ctl;
                    });
                    return _cargoTemperatureLogs;
                });

                const tankDetails = {
                    cargoNumber: cargoInTankItem.cargoNumber,
                    tradeName: cargoInTankItem.tradeName,
                    tank: tank,
                    cargoLogs: logs,
                    category: cargoInTankItem.cargoTempCategory,
                } as TankDetails;

                list.push(tankDetails);
            }

            const sortedList = list as TankDetails[];
            sortedList.sort((a, b) => (a.cargoNumber > b.cargoNumber ? 1 : -1));

            return sortedList;
        }, [] as TankDetails[]);

        const _vm: CargoTemperatureControlVM = {
            cargoTanks: _cargoTanks,
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        };

        setVM({ ..._vm });
    };

    const changeSort = (clickedColumn: SortColumnEnum) => {
        if (clickedColumn === sortColumn) {
            setSortDesc(!sortDesc);
        } else {
            setSortColumn(clickedColumn);
        }
    };

    const getInputFields = (cargoNumber: string) => {
        const fields = cargoTemperatureConfigs
            ?.find((c) => c.cargoNumber === cargoNumber)
            ?.inputFields.sort((a, b) => a.cargoTemperatureInputFieldId - b.cargoTemperatureInputFieldId);
        if (fields && fields?.length > 0) return fields.concat(cargoTemperatureInputFields[3]);
        else return fields;
    };

    const getLogTimes = (cargoNumber: string) => (cargoTemperatureConfigs?.find((c) => c.cargoNumber === cargoNumber)?.logTimes ?? []).sort();
    const getNumberOfInputFields = (cargoNumber: string) => getInputFields(cargoNumber)?.length ?? 0;

    const onCargoLogValueChange = (changeObject: InputChangeData) => {
        const value = +changeObject.event.target.value;

        changeObject.dataObject.value = value;

        const _cargoTempLogs = vm.cargoTanks.flatMap((tankDetails) => tankDetails.cargoLogs.flat());
        const _cargoTempLogDate = populatedCargoTempLogDates.find((d) => isDateSame(d.logDate, selectedCargoTempDate));
        const validation = validateCargoTempLog(_cargoTempLogDate, _cargoTempLogs, cargoTemperatureConfigs);
        _cargoTempLogDate.isValidated = validation;

        const cargoTemperatureReportLog = vm.cargoTanks.flatMap((ct) => ct.cargoLogs.flatMap((cl) => cl));
        setCargoTempLogReportLog([...cargoTemperatureReportLog]);
    };

    const onCargoLogIsOnBoardChange = (event: ChangeEvent<HTMLInputElement>, cargoTemperatureLog: CargoTemperatureLogEntry) => {
        const value = event.target.checked;
        cargoTemperatureLog.isOnBoard = value;

        const _cargoTempLogs = vm.cargoTanks.flatMap((tankDetails) => tankDetails.cargoLogs.flat());
        const _cargoTempLogDate = populatedCargoTempLogDates.find((d) => isDateSame(d.logDate, selectedCargoTempDate));
        const validation = validateCargoTempLog(_cargoTempLogDate, _cargoTempLogs, cargoTemperatureConfigs);
        _cargoTempLogDate.isValidated = validation;

        const cargoTemperatureReportLog = vm.cargoTanks.flatMap((ct) => ct.cargoLogs.flatMap((cl) => cl));
        setCargoTempLogReportLog([...cargoTemperatureReportLog]);
    };

    const onCargoLogCommentChange = (comment: string, cargoTemperatureLog: CargoTemperatureLogEntry) => {
        setCommentInput(comment);
        cargoTemperatureLog.comment = comment;
        const cargoTemperatureReportLog = vm.cargoTanks.flatMap((ct) => ct.cargoLogs.flatMap((cl) => cl));
        setCargoTempLogReportLog([...cargoTemperatureReportLog]);
    };

    const openComment = (cargoLog: CargoTemperatureLogEntry) => {
        if (editComment && editCommentCargoLog && editCommentCargoLog.cargoTemperatureLogId === cargoLog.cargoTemperatureLogId) {
            setEditCommentCargoLog(null);
            setCommentInput('');
            setEditComment(false);
        } else {
            setEditCommentCargoLog(cargoLog);
            setCommentInput(cargoLog?.comment ?? '');
            setEditComment(true);
        }
    };

    return (
        <>
            {selectedCargoTempDate && hasDayCargo && (
                <Wrapper key={selectedCargoTempDate.toString()}>
                    <CargoLogHeader hasMissingConfigs={hasMissingConfigs} />
                    <TableWrapper hasNewCargo={hasMissingConfigs}>
                        {selectedTab === tabEnum.config && <CargoTemperatureConfigComponent cargoTanks={vm.cargoTanks} />}
                        {selectedTab === tabEnum.reporting && (
                            <CargoTempLogTable
                                cargoTanks={vm.cargoTanks}
                                activeLogSchedules={activeLogSchedules}
                                sortColumn={sortColumn}
                                sortDesc={sortDesc}
                                isCommentsVisible={isCommentsVisible}
                                isReadOnly={isReadOnly}
                                oxygenUnitType={CargoTempLogOxygenUnitEnum.PerCent}
                                editComment={editComment}
                                editCommentCargoLog={editCommentCargoLog}
                                commentInput={commentInput}
                                cargoTemperatureInputFields={cargoTemperatureInputFields}
                                cargoTemperatureConfigs={cargoTemperatureConfigs}
                                changeSort={changeSort}
                                onCargoLogValueChange={onCargoLogValueChange}
                                onCargoLogIsOnBoardChange={onCargoLogIsOnBoardChange}
                                onCargoLogCommentChange={onCargoLogCommentChange}
                                openComment={openComment}
                                getNumberOfInputFields={getNumberOfInputFields}
                                date={selectedCargoTempDate}
                            />
                        )}
                        {selectedTab === tabEnum.export && <CargoTempLogExport />}
                    </TableWrapper>
                </Wrapper>
            )}
            {/* Show loading state while data is being loaded */}
            {(isLoadingType('cargoTempLogDates') || isLoadingType('cargoTemperatureLogs')) && (
                <InfoText>
                    <i className="icon">⏳</i>Loading cargo temperature data...
                </InfoText>
            )}
            {/* Show no cargo message only when definitely no cargo and not loading */}
            {!isLoadingType('cargoTempLogDates') && !isLoadingType('cargoTemperatureLogs') && selectedCargoTempDate && hasDayCargo === false && (
                <InfoText>
                    <i className="icon">{CargoTempLogStatusIcon(CargoTempLogStatusEnum.noCargo)}</i>No cargo for selected date. If this is wrong,
                    please check reported events
                </InfoText>
            )}
            {/* Show no dates message only when no dates available and not loading */}
            {!isLoadingType('cargoTempLogDates') && !isLoadingType('cargoTemperatureLogs') && hasCargoDates === false && (
                <InfoText>
                    <i className="icon">{CargoTempLogStatusIcon(CargoTempLogStatusEnum.noCargo)}</i>No cargo temperature log dates available for the
                    selected period.
                </InfoText>
            )}
        </>
    );
};

export default CargoTempLogReportingComponent;
